import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const FIELDS = [
  ["first_deposit_bonus_percent", "Signup / First bonus (%)", 100],
  ["regular_deposit_bonus_percent", "Regular bonus (%)", 20],
];

function commonRates(games) {
  return Object.fromEntries(FIELDS.map(([key, , fallback]) => {
    const values = new Set(games.map((game) => Number(game[key] ?? fallback)));
    return [key, values.size === 1 ? String([...values][0]) : ""];
  }));
}

export default function DepositBonusControl({ games, disabled = false, onApplied, onSavingChange }) {
  const [rates, setRates] = useState(() => commonRates(games));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!dirty) setRates(commonRates(games));
  }, [games, dirty]);

  const save = async (event) => {
    event.preventDefault();
    if (saving || disabled || !games.length) return;
    const payload = Object.fromEntries(FIELDS.map(([key]) => [key, Number(rates[key])]));
    if (FIELDS.some(([key]) => rates[key].trim() === "" || !Number.isInteger(payload[key]) || payload[key] < 0 || payload[key] > 1000)) {
      toast.error("Enter both bonus rates as whole numbers from 0 to 1000.");
      return;
    }
    setSaving(true);
    onSavingChange?.(true);
    try {
      const { data } = await api.put("/admin/game-bonuses", payload);
      toast.success(`Bonus rates applied to all ${data.matched_count} games`);
      try {
        await onApplied();
        setDirty(false);
      } catch {
        toast.error("Bonus rates saved. Refresh the page to see the updated games.");
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  return (
    <form onSubmit={save} className="mb-6 rounded-3xl border border-[#72ACD8]/20 bg-[#0B1722] p-5 sm:p-7" aria-label="Deposit bonus control">
      <p className="text-xs font-black uppercase tracking-[0.24em] text-[#82B8DF]">Deposit bonus control</p>
      <h2 className="mt-2 text-2xl font-bold tracking-tight text-white">Signup bonus + regular bonus</h2>
      <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-400 sm:text-base">
        Set both bonus rates in one place for all games. Each game's first eligible cash load uses signup bonus; later cash loads use regular bonus. Freeplay does not earn a deposit bonus.
      </p>
      <div className="mt-6 grid gap-5 md:grid-cols-2">
        {FIELDS.map(([key, label]) => (
          <label key={key} className="block">
            <span className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-400">{label}</span>
            <input type="number" required min="0" max="1000" step="1" value={rates[key]}
              disabled={saving || disabled || !games.length}
              placeholder={games.length ? "Mixed rates — enter a value" : "No games available"}
              onChange={(event) => { setDirty(true); setRates((current) => ({ ...current, [key]: event.target.value })); }}
              className="h-14 w-full rounded-full border border-white/10 bg-white/[0.055] px-5 text-xl font-bold text-white outline-none placeholder:text-sm placeholder:font-normal focus:border-[#82B8DF] disabled:opacity-50" />
          </label>
        ))}
      </div>
      <button type="submit" disabled={saving || disabled || !games.length}
        className="mt-6 min-h-14 w-full rounded-full bg-[#72ACD8] px-6 py-4 text-base font-bold text-white transition hover:bg-[#83B9E0] disabled:cursor-not-allowed disabled:opacity-50">
        {saving ? "Applying…" : "Apply to all games"}
      </button>
    </form>
  );
}
